/**
 * A character, as the simulation needs one.
 *
 * The profile on disk is a name, an inventory, a stat block and a progress record. Two of those
 * four are what a body is actually stepped from, and this is the pair. It exists so that a mode
 * takes one thing rather than threading two through every constructor, and so that the boundary
 * between `save/` and a mode is a value with a name rather than a habit.
 *
 * The stats here are the character's own, before anything is worn and before any perk is counted.
 * `characterBuild` is what turns this plus a backpack plus a moment into the numbers the pipeline
 * reads, and it is the only thing that should.
 */

import { NEW_PROGRESS } from '@/sim/progression/progressState';
import { PERK_ORDER } from '@/sim/progression/perkRegistry';
import { STAT_ORDER, statValue } from '@/sim/combat/statContract';
import type { ProgressState } from '@/sim/progression/progressState';
import type { StatBlock } from '@/sim/combat/statContract';

export interface CharacterSheet {
  /** Earned stats, before gear and before perks. */
  readonly stats: StatBlock;
  readonly progress: ProgressState;
}

/** Somebody who has never played, at whatever a fresh body of their kind starts from. */
export function newSheet(stats: StatBlock): CharacterSheet {
  return { stats, progress: NEW_PROGRESS };
}

/**
 * Whether two sheets describe the same character.
 *
 * A sheet arrives from a save and from the wire, so the same character reaches a comparison as two
 * different objects more often than not: a guest re-announcing itself on every reconnect is the
 * common case. Walked in `STAT_ORDER` and `PERK_ORDER` for the usual reason, that two machines
 * comparing the same pair have to reach the same answer.
 */
export function sheetsEqual(left: CharacterSheet, right: CharacterSheet): boolean {
  if (left === right) return true;
  if (left.progress.xp !== right.progress.xp) return false;
  if (STAT_ORDER.some((id) => statValue(left.stats, id) !== statValue(right.stats, id))) {
    return false;
  }
  return PERK_ORDER.every(
    (id) => (left.progress.perkRanks[id] ?? 0) === (right.progress.perkRanks[id] ?? 0),
  );
}
